import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getMyCustomerReviews, submitCustomerReview, GetMyCustomerReviewsOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Star, Plus, CheckCircle, Clock, ExternalLink, Loader2, Gift } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

const PLATFORM_ICONS: Record<string, string> = { Google: '🔍', TripAdvisor: '🦉', Facebook: '👍', Other: '⭐' };
const STATUS_STYLE: Record<string, string> = {
  Pending: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  Verified: 'bg-green-500/10 text-green-700 border-green-500/20',
  Rejected: 'bg-red-500/10 text-red-700 border-red-500/20',
};

export default function CustomerReviews() {
  const { user } = useAuth();
  const [data, setData] = useState<GetMyCustomerReviewsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [form, setForm] = useState({ reviewerName: '', platform: 'Google' as const, reviewUrl: '', reviewText: '', reviewDate: new Date().toISOString().split('T')[0] });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    if (!user) return;
    getMyCustomerReviews({}).then(setData).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  const handleSubmit = async () => {
    if (!form.reviewerName || !form.reviewText) { toast.error('Reviewer name and review text are required'); return; }
    setSubmitting(true);
    try {
      await submitCustomerReview({ reviewerName: form.reviewerName, platform: form.platform as any, reviewUrl: form.reviewUrl || undefined, reviewText: form.reviewText, reviewDate: form.reviewDate });
      toast.success('Review submitted! Management will verify it shortly.');
      setSubmitOpen(false);
      setForm({ reviewerName: '', platform: 'Google', reviewUrl: '', reviewText: '', reviewDate: new Date().toISOString().split('T')[0] });
      load();
    } catch (e: any) { toast.error(e.message || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="px-6 py-8 max-w-2xl mx-auto space-y-3"><Skeleton className="h-8 w-48" />{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-foreground flex items-center gap-2"><Star className="w-5 h-5 text-amber-500" /> My Customer Reviews</h1>
            <p className="text-sm text-muted-foreground mt-1">Submit reviews mentioning you — verified reviews earn a £5 employee gift card</p>
          </div>
          <Button onClick={() => setSubmitOpen(true)} className="gap-1.5 shrink-0"><Plus className="w-4 h-4" /> Submit Review</Button>
        </div>
      </div>

      <div className="px-6 py-6 max-w-2xl mx-auto space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Submitted', value: data?.totalReviews || 0, icon: '📝' },
            { label: 'Verified', value: data?.verifiedReviews || 0, icon: '✅' },
            { label: 'Gift Cards', value: `£${data?.totalGiftCardValue || 0}`, icon: '🎁' },
          ].map(s => (
            <div key={s.label} className="border border-border rounded-xl bg-card p-4 text-center">
              <p className="text-xl">{s.icon}</p>
              <p className="text-xl font-bold text-foreground mt-1">{s.value}</p>
              <p className="text-xs text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 text-sm text-muted-foreground">
          <p><Gift className="w-4 h-4 inline mr-1.5 text-primary" />When a customer leaves a review on Google, TripAdvisor or Facebook mentioning you by name, submit it here. Once verified by management, you'll receive a <strong className="text-foreground">£5 Employee Gift Card</strong> credit. 🎉</p>
        </div>

        {(data?.reviews.length ?? 0) === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <Star className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">No reviews submitted yet</p>
            <Button onClick={() => setSubmitOpen(true)}><Plus className="w-4 h-4 mr-1" /> Submit your first review</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {data?.reviews.map(r => (
              <div key={r.id} className="border border-border rounded-xl bg-card p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{PLATFORM_ICONS[r.platform]} {r.reviewerName}</p>
                    <p className="text-xs text-muted-foreground">{r.platform} · {fmtDate(r.reviewDate)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.giftCardAwarded && <span className="text-xs text-green-700 bg-green-500/10 border border-green-500/20 rounded px-1.5 py-0.5">🎁 £{r.giftCardAmount} awarded</span>}
                    <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLE[r.status] || 'bg-muted border-border text-muted-foreground'}`}>{r.status}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{r.reviewText}</p>
                {r.reviewUrl && (
                  <a href={r.reviewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2">
                    <ExternalLink className="w-3 h-3" /> View original review
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={submitOpen} onOpenChange={v => !submitting && setSubmitOpen(v)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-500" /> Submit a Customer Review</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2 text-sm">
            <p className="text-xs text-muted-foreground">Submit reviews where the customer has mentioned you by name. Management will verify and award gift card credit.</p>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Customer / Reviewer Name *</label>
              <Input value={form.reviewerName} onChange={e => setForm(f => ({ ...f, reviewerName: e.target.value }))} placeholder="The name used in the review" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Platform</label>
                <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v as any }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Google', 'TripAdvisor', 'Facebook', 'Other'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Review Date</label>
                <Input type="date" value={form.reviewDate} onChange={e => setForm(f => ({ ...f, reviewDate: e.target.value }))} /></div>
            </div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Review URL (optional)</label>
              <Input value={form.reviewUrl} onChange={e => setForm(f => ({ ...f, reviewUrl: e.target.value }))} placeholder="https://..." /></div>
            <div><label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Review Text (paste here) *</label>
              <Textarea value={form.reviewText} onChange={e => setForm(f => ({ ...f, reviewText: e.target.value }))} placeholder="Paste the full review text..." className="min-h-24" /></div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setSubmitOpen(false)} disabled={submitting}>Cancel</Button>
              <Button className="flex-1" onClick={handleSubmit} disabled={submitting}>
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Submit Review'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
